/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { AttributeGroup, DocumentGroup, VideoDocumentModel } from "@eisinc/App/Store/Reducers/ProductDetails/state";
import logger from "@insite/client-framework/Logger";
import { getProductState, getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import cloneDeep from "lodash/cloneDeep";

export function getProductCustomState(state: CustomAppState, productId: string) {
    return state.customProductDetails.byId[productId];
}

export function getProductAttributeGroups(state: CustomAppState, product: ProductModel): AttributeGroup[] | undefined {
    if (product) {
        const customProductState = state.customProductDetails.byId[product.id];
        if (customProductState) {
            return customProductState.attributeGroups;
        }
    }
}

export function getProductDocumentGroups(state: CustomAppState, product: ProductModel): DocumentGroup[] | undefined {
    if (product) {
        const customProductState = state.customProductDetails.byId[product.id];
        if (customProductState) {
            return customProductState.documentGroups;
        }
    }
}

export function getProductProperties(state: CustomAppState, product: ProductModel): any {
    if (product) {
        const customProductState = state.customProductDetails.byId[product.id];

        if (customProductState) {
            return customProductState.properties;
        }
    }
}

export function getProductEmbedDocumentIds(state: CustomAppState, product: ProductModel): string[] {
    if (!product) {
        return [];
    }

    const customProductState = state.customProductDetails.byId[product.id];
    if (!customProductState || !customProductState.properties) {
        return [];
    }

    const { properties: productProperties } = customProductState;
    const parentProductId = productProperties.parentProductId;

    let productEmbedDocumentIds: string[] = [];
    let parentProductEmbedDocumentIds: string[] = [];

    try {
        productEmbedDocumentIds = JSON.parse(productProperties.embedDocumentIds || "[]");
    } catch (e) {
        logger.error("Failed to parse product embedDocumentIds:", e);
    }

    if (parentProductId) {
        const customParentProductState = state.customProductDetails.byId[parentProductId];
        if (customParentProductState && customParentProductState.properties) {
            try {
                parentProductEmbedDocumentIds = JSON.parse(
                    customParentProductState.properties.embedDocumentIds || "[]",
                );
            } catch (e) {
                logger.error("Failed to parse parent product embedDocumentIds:", e);
            }
        }
    }

    return [...parentProductEmbedDocumentIds, ...productEmbedDocumentIds];
}

export function getProductVideos(state: CustomAppState, product: ProductModel): VideoDocumentModel[] | undefined {
    if (product) {
        const customProductState = state.customProductDetails.byId[product.id];
        if (customProductState) {
            return customProductState.videos;
        }
    }
}

export function getSelectedVideoIndex(state: CustomAppState, product: ProductModel) {
    let selectedVideoIndex = -1;
    if (product) {
        const customProductState = state.customProductDetails.byId[product.id];
        if (customProductState) {
            selectedVideoIndex = customProductState.selectedVideoIndex;
        }
    }
    return selectedVideoIndex;
}

export function getSelectedVideo(state: CustomAppState, product: ProductModel): VideoDocumentModel | undefined {
    if (product) {
        const customProductState = state.customProductDetails.byId[product.id];
        if (customProductState && customProductState.selectedVideoIndex > -1) {
            return customProductState.videos[customProductState.selectedVideoIndex];
        }
    }
}

// it currently doesn't appear that we need to memoize this. We may need to later after we resolve some issues with DataState not caching results as well as it could.
export function getComputedVariantProduct(
    parentProductState: ReturnType<typeof getProductStateByPath>,
    variantProductState: ReturnType<typeof getProductState>,
) {
    if (!variantProductState.value) {
        return parentProductState;
    }

    if (!parentProductState.value) {
        return parentProductState;
    }

    if (parentProductState.value.id === variantProductState.value.id) {
        return parentProductState;
    }

    const { value: variantProduct } = variantProductState;

    const computedProduct = cloneDeep(parentProductState.value);
    Object.assign(computedProduct, {
        // <xng-change>
        productNumber: variantProduct.productNumber || computedProduct.productNumber,
        smallImagePath: variantProduct.smallImagePath || computedProduct.smallImagePath,
        mediumImagePath: variantProduct.mediumImagePath || computedProduct.mediumImagePath,
        largeImagePath: variantProduct.largeImagePath || computedProduct.largeImagePath,
        imageAltText: variantProduct.imageAltText || computedProduct.imageAltText,
        id: variantProduct.id,
        quoteRequired: variantProduct.quoteRequired,
        productTitle: variantProduct.productTitle,
        images:
            variantProduct.images && variantProduct.images.length > 0 ? variantProduct.images : computedProduct.images,
        trackInventory: variantProduct.trackInventory,
        minimumOrderQty: variantProduct.minimumOrderQty,
        canonicalUrl: variantProduct.canonicalUrl,
        unitOfMeasures: variantProduct.unitOfMeasures,
        isVariantParent: false,
        detail: {
            ...variantProduct.detail,
            modelNumber: variantProduct.detail?.modelNumber || computedProduct.detail?.modelNumber,
            productCode: variantProduct.detail?.productCode || computedProduct.detail?.productCode,
            upcCode: variantProduct.detail?.upcCode || computedProduct.detail?.upcCode,
        },
        properties: {
            ...variantProduct.properties,
            specNumber: variantProduct.properties.specNumber || computedProduct.properties.specNumber,
        },
        specifications:
            variantProduct.specifications && variantProduct.specifications.length > 0
                ? variantProduct.specifications
                : computedProduct.specifications,
        content: variantProduct.content || computedProduct.content,
        attributeTypes: variantProduct.attributeTypes,
        manufacturerItem: variantProduct.manufacturerItem || computedProduct.manufacturerItem,
        customerProductNumber: variantProduct.customerProductNumber || computedProduct.customerProductNumber,
        packDescription: variantProduct.packDescription,
        canAddToWishlist: variantProduct.canAddToWishlist,
        canAddToCart: variantProduct.canAddToCart,
        brand: variantProduct.brand,
        // </xng-change>
    });

    return {
        isLoading: parentProductState.isLoading,
        id: variantProductState.id,
        value: computedProduct,
    };
}
